import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Divider, Typography, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { dateToDecimalYear } from "./utils/rulXAxis";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import DonutChart from "./DonutChart";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { getRULScatterData, getRULDistribution } from "../../../Api/BatteryHealthApi";
import { useRULBuckets, useRULServerBucketUnits, resolveRULCategory } from "./utils/rulBuckets";
import { getBucketDisplayLabel } from "./utils/unavailableBucket";
import RULScatterChart from "../../../Charts/Scatter/RULScatterChart";
import CODE from "../../../Static/Constants/StatusCodes";
import { ALL_Y_FILTERS, Y_KEY, RUL_BUCKET_KEYS } from "./utils/rulConstants";
import { smartTooltipPos } from "./utils/chartUtils";
import { getLegendSwatchSx } from "../../../Charts/Scatter/scatterBucketUtils";
import CellChemistryQuickFilter from "../Components/CellChemistryQuickFilter";
import { buildCellChemistryFilters } from "./utils/cellChemistryFilter";

const RUL_SCATTER_CHART_ID = "rul-scatter-chart";


// ─── Component ────────────────────────────────────────────────────────────────
const RemainingUsefulLifeChart = ({
  globalFilters = {},
  onDetailsClick,
  chartConfig = {},
  cellChemistryOptions = [],
  cellChemistry = null,
  onCellChemistryChange,
}) => {
  const rulBuckets = useRULBuckets();
  // Applied to both the donut and the scatter; [] when All is selected.
  const chemistryFilters = useMemo(() => buildCellChemistryFilters(cellChemistry), [cellChemistry]);
  const rulServerBucketUnits = useRULServerBucketUnits();

  const yFilters = useMemo(() => {
    const supported = chartConfig?.rulScatterChart?.supportedUnits;
    if (!supported || supported.length === 0) return ALL_Y_FILTERS;
    return ALL_Y_FILTERS.filter((f) => supported.includes(f.key));
  }, [chartConfig]);

  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [activeYFilter,      setActiveYFilter]      = useState(() => {
    const supported = chartConfig?.rulScatterChart?.supportedUnits;
    if (supported && supported.length > 0) return supported[0];
    return "months";
  });

  // Active bucket set for the current filter
  const activeCategories = useMemo(() => rulBuckets[activeYFilter] ?? [], [rulBuckets, activeYFilter]);

  // ─── Scatter API state ────────────────────────────────────────────────────
  const [rulBatteries,   setRulBatteries]   = useState([]);
  const [scatterLoading, setScatterLoading] = useState(true);
  const [scatterError,   setScatterError]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setScatterLoading(true);
      setScatterError(false);
      try {
        const res = await getRULScatterData(globalFilters, activeYFilter, chemistryFilters, chartConfig?.rulScatterChart?.supportedUnits ?? []);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items      = res?.response?.data ?? [];
          const activeYKey = Y_KEY[activeYFilter];
          const batteries  = items.map(({ xLabel, metricsArray, metaInfo }) => {
            const getVal    = (type) => metricsArray?.find((m) => m.type === type)?.value ?? null;
            const rawDays   = getVal("RULCalendarAgeDaysPredicted");
            const rulMonths = rawDays != null ? parseFloat((rawDays / 30.44).toFixed(1)) : null;
            const rulCycles = getVal("RULCyclesPredicted");
            const rulKm     = getVal("RULKmPredicted");
            const mfgDate        = metaInfo?.manufacturingDate;
            const mfgYear        = mfgDate ? dateToDecimalYear(new Date(mfgDate)) : null;
            // Missing / invalid / pre-2000 dates have no x: range-bucket batteries without one
            // are not plotted; "RUL Unavailable" ones are pinned to the x-axis minimum.
            const mfgDecimalYear = mfgYear != null && isFinite(mfgYear) && mfgYear >= 2000 ? mfgYear : null;
            return {
              batteryID: xLabel, deviceID: metaInfo?.deviceID, rulMonths, rulCycles, rulKm,
              [activeYKey]: activeYFilter === "months" ? rulMonths : activeYFilter === "cycles" ? rulCycles : rulKm,
              mfgDecimalYear,
              // Raw metaInfo: the active unit's server bucket label is read from it at
              // classification time (resolveRULCategory), only for units the server labels.
              rulMetaInfo:   metaInfo ?? null,
              rulBucketUnit: activeYFilter,
            };
          });
          setRulBatteries(batteries);
        } else {
          if (!cancelled) setScatterError(true);
        }
      } catch (_) {
        if (!cancelled) setScatterError(true);
      } finally {
        if (!cancelled) setScatterLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [globalFilters, activeYFilter, chemistryFilters]);

  // ─── Donut API state — re-fetches when unit tab changes ──────────────────
  const [donutCounts,  setDonutCounts]  = useState({});
  const [donutTotal,   setDonutTotal]   = useState(0);
  const [donutLoading, setDonutLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchDonut = async () => {
      setDonutLoading(true);
      try {
        const rulBucketKey = RUL_BUCKET_KEYS[activeYFilter];
        const res = await getRULDistribution(globalFilters, rulBucketKey, chemistryFilters);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items = res?.response?.data ?? [];
          const counts = {};
          let total = 0;
          items.forEach(({ xLabel, metricsArray }) => {
            const count = metricsArray?.find((m) => m.type === "DistinctVehicleCount")?.value ?? 0;
            counts[xLabel] = count;
            total += count;
          });
          setDonutCounts(counts);
          setDonutTotal(total);
        } else {
          if (!cancelled) { setDonutCounts({}); setDonutTotal(0); }
        }
      } catch (_) {
        if (!cancelled) { setDonutCounts({}); setDonutTotal(0); }
      } finally {
        if (!cancelled) setDonutLoading(false);
      }
    };
    fetchDonut();
    return () => { cancelled = true; };
  }, [globalFilters, activeYFilter, chemistryFilters]);

  const [donutHover, setDonutHover] = useState(null);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedCategories(new Set());
    setDonutHover(null);
  }, [activeYFilter]);

  // ─── Category selection ───────────────────────────────────────────────────
  const selectCategory = useCallback((name) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setDonutHover(null);
  }, []);

  // Reset also clears the chemistry back to All.
  const hasActiveSelection = selectedCategories.size > 0 || cellChemistry != null;
  const resetSelection = useCallback(() => {
    setSelectedCategories(new Set());
    setDonutHover(null);
    if (cellChemistry != null) onCellChemistryChange?.(null);
  }, [cellChemistry, onCellChemistryChange]);

  const yKey = Y_KEY[activeYFilter];

  const donutCenterCount = useMemo(() => {
    if (selectedCategories.size === 0) return donutTotal;
    return [...selectedCategories].reduce(
      (sum, name) => sum + (donutCounts[name] ?? 0), 0
    );
  }, [selectedCategories, donutCounts, donutTotal]);

  const donutCenterLabel = useMemo(() => {
    const allSelected = selectedCategories.size === activeCategories.length;
    if (selectedCategories.size === 0 || allSelected)
      return { primary: "All Batteries", secondary: null };
    if (selectedCategories.size === 1)
      return { primary: getBucketDisplayLabel([...selectedCategories][0], activeCategories), secondary: null };
    const [first, ...rest] = [...selectedCategories];
    return { primary: getBucketDisplayLabel(first, activeCategories), secondary: `+ ${rest.length} more` };
  }, [selectedCategories, activeCategories]);

  // ─── Scatter visible groups — passed to RULScatterChart ──────────────────
  // Server label (same boundaries as the donut) for months / cycles when the org defines
  // those buckets; km and orgs without bucket meta classify client-side. Null / negative
  // RUL resolves to "RUL Unavailable".
  const categorisedBatteries = useMemo(() => {
    const useServerLabel = rulServerBucketUnits[activeYFilter] === true;
    return rulBatteries.map((b) => ({
      ...b,
      rulCategory: resolveRULCategory(
        b.rulMetaInfo, activeYFilter, b[yKey], activeCategories,
        useServerLabel && b.rulBucketUnit === activeYFilter
      ),
    }));
  }, [rulBatteries, yKey, activeCategories, activeYFilter, rulServerBucketUnits]);

  const batteriesByVisibleCategory = useMemo(
    () =>
      activeCategories
        .filter((cat) => selectedCategories.size === 0 || selectedCategories.has(cat.name))
        .map((cat) => ({
          category:  cat,
          batteries: categorisedBatteries.filter((b) => b.rulCategory === cat.name),
        })),
    [selectedCategories, activeCategories, categorisedBatteries]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E6E6E6", overflow: "clip" }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex", alignItems: "center",
          px: "28px", pt: "24px", pb: "8px", gap: "12px",
        }}
      >
        {/* Title */}
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#2E2E2E", lineHeight: "24px", flexShrink: 0 }}>
          Remaining Useful Life (RUL)
        </Typography>

        {/* Y-axis filters — segmented control, right of title */}
        <Box
          sx={{
            display: "flex", alignItems: "center",
            bgcolor: "#f1f1f1fa", borderRadius: "8px",
            p: "3px", gap: "7px", flexShrink: 0,
          }}
        >
          {yFilters.map((filter) => {
            const isActive = activeYFilter === filter.key;
            return (
              <Box
                key={filter.key}
                onClick={() => setActiveYFilter(filter.key)}
                sx={{
                  px: "12px", py: "4px", borderRadius: "6px", cursor: "pointer",
                  border: isActive ? "1px solid #6D72F6" : "none",
                  bgcolor: isActive ? "#EEF0FF" : "transparent",
                  boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)" : "none",
                  transition: "all 0.15s", userSelect: "none",
                  "&:hover": { bgcolor: isActive ? "#EEF0FF" : "rgba(0,0,0,0.04)" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#6D72F6" : "#6B7280",
                    lineHeight: "20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {filter.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Details link — right of filters */}
        <Box
          onClick={onDetailsClick}
          sx={{
            display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0,
            "&:hover": {
              "& .MuiTypography-root": { color: "#4D44D8" },
              "& .MuiSvgIcon-root":    { color: "#4D44D8" },
            },
          }}
        >
          <Typography variant="fw500s12lh20c000000" sx={{ color: "#6D72F6" }}>
            Details
          </Typography>
          <ChevronRightIcon sx={{ color: "#6D72F6", fontSize: "18px" }} />
        </Box>

        {/* Cell Chemistry + Reset — far right */}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
          <CellChemistryQuickFilter
            value={cellChemistry}
            options={cellChemistryOptions}
            onChange={onCellChemistryChange}
            testIdPrefix="rul-overview"
          />
          <Button
            variant="outlined"
            size="small"
            onClick={resetSelection}
            disabled={!hasActiveSelection}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              minWidth: "auto",
              flexShrink: 0,
              px: 1.5,
              py: "3px",
              borderRadius: "6px",
              color:       hasActiveSelection ? "#6D72F6" : "#9E9E9E",
              borderColor: hasActiveSelection ? "#6D72F6" : "#D1D5DB",
              "&:hover": { borderColor: "#6D72F6", bgcolor: "#F5F5FF" },
              "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#9E9E9E" },
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* ── Shared legend ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "center", px: "28px", pt: "12px", pb: "12px" }}>
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "16px" }}>
          {activeCategories.map((cat) => {
            const isSelected  = selectedCategories.has(cat.name);
            const hasSelection = selectedCategories.size > 0;
            const isFaded     = hasSelection && !isSelected;
            return (
              <Box
                key={cat.name}
                data-testid={`rul-overview-legend-${cat.name}`}
                onClick={() => selectCategory(cat.name)}
                sx={{
                  display: "flex", alignItems: "center", gap: "5px",
                  cursor: "pointer", opacity: isFaded ? 0.35 : 1,
                  transition: "opacity 0.18s", userSelect: "none",
                  "&:hover": { opacity: isFaded ? 0.55 : 0.78 },
                }}
              >
                <Box sx={getLegendSwatchSx(cat, isFaded)} />
                <Typography
                  sx={{
                    fontSize: "12px", color: "#444B51", whiteSpace: "nowrap",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {cat.displayLabel ?? cat.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Charts row ────────────────────────────────────────────────────── */}
      {scatterLoading || donutLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "350px" }}>
          <CartLoader />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto", pb: "24px", width: "100%" }}>
          <Box
            sx={{
              display: "flex", alignItems: "center",
              px: "28px", gap: "16px", minWidth: "900px",
            }}
          >
            {/* ── Donut ─────────────────────────────────────────────────── */}
            <Box sx={{ width: "270px", flexShrink: 0, position: "relative" }}>
              <DonutChart
                series={activeCategories.map((cat) => donutCounts[cat.name] ?? 0)}
                colors={activeCategories.map((cat) => cat.color)}
                categoryNames={activeCategories.map((cat) => cat.name)}
                selectedCategories={selectedCategories}
                onSelect={selectCategory}
                onSliceMouseEnter={(e, i) =>
                  setDonutHover({ clientX: e.clientX, clientY: e.clientY, seriesIndex: i })
                }
                onSliceMouseLeave={() => setDonutHover(null)}
                size={270}
                gapDeg={0}
                sliceStrokeWidth={0}
              />

              {/* Center overlay */}
              <Box
                sx={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center", pointerEvents: "none", width: "130px",
                }}
              >
                <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#2E2E2E", lineHeight: 1 }}>
                  {donutCenterCount}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#9E9E9E", mt: "4px", lineHeight: 1.4 }}>
                  {donutCenterLabel.primary}
                </Typography>
                {donutCenterLabel.secondary && (
                  <Typography sx={{ fontSize: "11px", color: "#9E9E9E", lineHeight: 1.4 }}>
                    {donutCenterLabel.secondary}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* ── Divider ───────────────────────────────────────────────── */}
            <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mr:"15px", ml :"15px" }} />

            {/* ── Scatter ───────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, minWidth: "580px" }}>
              <RULScatterChart
                batteriesByVisibleCategory={batteriesByVisibleCategory}
                selectedCategories={selectedCategories}
                onCategorySelect={selectCategory}
                activeYFilter={activeYFilter}
                supportedUnits={chartConfig?.rulScatterChart?.supportedUnits}
                chartId={RUL_SCATTER_CHART_ID}
                height={350}
                showLegend={false}
                onDotClick={(battery) => {
                  if (!battery?.batteryID) return;
                  const hash = encryptTheParams({ batteryID: battery.batteryID, tabValue: "health" });
                  window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Donut hover tooltip ───────────────────────────────────────────── */}
      {donutHover && (() => {
        const cat   = activeCategories[donutHover.seriesIndex];
        const count = donutCounts[cat?.name] ?? 0;
        if (!cat || !count) return null;
        const pct = parseFloat(((count / donutTotal) * 100).toFixed(2));
        const dateStr = new Date().toLocaleDateString("en-GB", {
          day: "2-digit", month: "short", year: "numeric",
        });
        const { left, top } = smartTooltipPos(donutHover.clientX, donutHover.clientY, 215, 100);
        return (
          <Box sx={{
            position: "fixed", left, top, zIndex: 9999,
            bgcolor: "#fff", border: "1px solid #E6E6E6", borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", p: "12px 16px",
            minWidth: "210px", pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E", mb: "10px" }}>
              {dateStr}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cat.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: "#444B51" }}>
                Percentage of Fleet:&nbsp;
                <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                  {pct}%
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ width: 8, height: 8, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: "#444B51" }}>
                Number of Vehicles:&nbsp;
                <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                  {count}
                </Typography>
              </Typography>
            </Box>
          </Box>
        );
      })()}

    </Box>
  );
};

export default RemainingUsefulLifeChart;
